import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  MessageSquare, 
  Send, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  Plus,
  Mail,
  MailOpen,
  Clock,
  User,
  Reply
} from "lucide-react";

export default function AdminMessagesPage() {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [composeOpen, setComposeOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [newMessage, setNewMessage] = useState({
    recipient_id: "",
    subject: "",
    content: ""
  });
  const [editMessage, setEditMessage] = useState({
    subject: "",
    content: ""
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchTenants();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/admin/tenants-for-messaging`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
      console.error("Failed to load tenants:", error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.recipient_id || !newMessage.subject || !newMessage.content) {
      toast.error("Please fill in all fields");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/admin/messages`, newMessage, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message sent successfully!");
      setComposeOpen(false);
      setNewMessage({ recipient_id: "", subject: "", content: "" });
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleUpdateMessage = async () => {
    if (!editMessage.subject || !editMessage.content) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.put(`${API}/admin/messages/${selectedMessage.id}`, editMessage, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message updated successfully!");
      setEditOpen(false);
      fetchMessages();
    } catch (error) {
      toast.error("Failed to update message");
    }
  };

  const handleDeleteMessage = async () => {
    try {
      await axios.delete(`${API}/admin/messages/${selectedMessage.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message deleted successfully!");
      setDeleteOpen(false);
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const openViewDialog = async (message) => {
    setSelectedMessage(message);
    setViewOpen(true);
    
    // Mark replies as read if there are unread replies
    if (message.has_unread_reply) {
      try {
        await axios.post(`${API}/admin/messages/${message.id}/mark-replies-read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Update local state
        setMessages(prev => prev.map(m => 
          m.id === message.id ? {...m, has_unread_reply: false} : m
        ));
      } catch (error) {
        console.error("Failed to mark replies as read:", error);
      }
    }
  };

  const openEditDialog = (message) => {
    setSelectedMessage(message);
    setEditMessage({
      subject: message.subject,
      content: message.content
    });
    setEditOpen(true);
  };

  const openDeleteDialog = (message) => {
    setSelectedMessage(message);
    setDeleteOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredMessages = messages.filter(msg =>
    msg.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-messages-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary">Messages</h1>
              <p className="text-text-muted mt-1">Send and manage messages to tenants</p>
            </div>
            <div className="flex gap-3">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  data-testid="search-messages-input"
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12 bg-white border-stone-200"
                />
              </div>
              <Button 
                onClick={() => setComposeOpen(true)}
                className="bg-primary hover:bg-primary-800 rounded-full h-12 px-6"
                data-testid="compose-message-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Message
              </Button>
            </div>
          </div>

          {/* Messages List */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                All Messages ({filteredMessages.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-primary">Loading...</div>
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <p className="text-text-muted">No messages yet</p>
                  <Button 
                    onClick={() => setComposeOpen(true)}
                    className="mt-4 rounded-full"
                  >
                    Send your first message
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredMessages.map((msg) => (
                    <div 
                      key={msg.id}
                      className={`rounded-xl p-4 border hover:shadow-md transition-shadow ${
                        msg.has_unread_reply 
                          ? 'bg-amber-50 border-amber-300' 
                          : 'bg-stone-50 border-stone-200'
                      }`}
                      data-testid={`message-card-${msg.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            msg.has_unread_reply 
                              ? 'bg-amber-500' 
                              : msg.is_read 
                                ? 'bg-green-100' 
                                : 'bg-blue-100'
                          }`}>
                            {msg.has_unread_reply ? (
                              <Reply className="w-5 h-5 text-white" />
                            ) : msg.is_read ? (
                              <MailOpen className="w-5 h-5 text-green-600" />
                            ) : (
                              <Mail className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-semibold text-primary truncate">
                                To: {msg.recipient_name}
                              </span>
                              {msg.has_unread_reply && (
                                <Badge className="bg-amber-500 text-white text-xs animate-pulse">
                                  New Reply!
                                </Badge>
                              )}
                              {msg.is_read && !msg.has_unread_reply && (
                                <Badge className="bg-green-100 text-green-800 text-xs">
                                  Read by Tenant
                                </Badge>
                              )}
                              {!msg.is_read && (
                                <Badge className="bg-blue-100 text-blue-800 text-xs">
                                  Not Read Yet
                                </Badge>
                              )}
                              {msg.replies?.length > 0 && (
                                <Badge className="bg-stone-100 text-stone-700 text-xs">
                                  {msg.replies.length} {msg.replies.length === 1 ? 'Reply' : 'Replies'}
                                </Badge>
                              )}
                            </div>
                            <h3 className="font-medium text-stone-800 truncate">{msg.subject}</h3>
                            <p className="text-sm text-text-muted truncate mt-1">{msg.content}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Sent: {formatDate(msg.created_at)}
                              </span>
                              {msg.is_read && msg.read_at && (
                                <span className="flex items-center gap-1 text-green-600">
                                  <Eye className="w-3 h-3" />
                                  Read: {formatDate(msg.read_at)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openViewDialog(msg)}
                            data-testid={`view-message-btn-${msg.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(msg)}
                            data-testid={`edit-message-btn-${msg.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog(msg)}
                            data-testid={`delete-message-btn-${msg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Compose Message Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              New Message
            </DialogTitle>
            <DialogDescription>
              Send a message to a tenant
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient *</Label>
              <Select 
                value={newMessage.recipient_id}
                onValueChange={(value) => setNewMessage({...newMessage, recipient_id: value})}
              >
                <SelectTrigger data-testid="recipient-select">
                  <SelectValue placeholder="Select a tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.full_name || tenant.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                data-testid="message-subject-input"
                placeholder="Enter message subject"
                value={newMessage.subject}
                onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                data-testid="message-content-input"
                placeholder="Write your message here..."
                value={newMessage.content}
                onChange={(e) => setNewMessage({...newMessage, content: e.target.value})}
                rows={5}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendMessage}
              disabled={sending}
              className="bg-primary hover:bg-primary-800"
              data-testid="send-message-btn"
            >
              {sending ? "Sending..." : "Send Message"}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Message Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Message Details</DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4 py-4">
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-5 h-5 text-primary" />
                  <span className="font-semibold">To: {selectedMessage.recipient_name}</span>
                  {selectedMessage.is_read ? (
                    <Badge className="bg-green-100 text-green-800 ml-auto">Read</Badge>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-800 ml-auto">Unread</Badge>
                  )}
                </div>
                <h3 className="text-lg font-semibold mb-2">{selectedMessage.subject}</h3>
                <p className="text-stone-600 whitespace-pre-wrap">{selectedMessage.content}</p>
                <p className="text-xs text-stone-400 mt-3">
                  Sent: {formatDate(selectedMessage.created_at)}
                </p>
              </div>
              
              {/* Replies Section */}
              {selectedMessage.replies?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Reply className="w-4 h-4" />
                    Replies ({selectedMessage.replies.length})
                  </h4>
                  {selectedMessage.replies.map((reply) => (
                    <div 
                      key={reply.id}
                      className="bg-green-50 rounded-xl p-4 border-l-4 border-green-500"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-green-800">{reply.sender_name}</span>
                        <Badge className="bg-green-200 text-green-900 text-xs">Tenant</Badge>
                      </div>
                      <p className="text-stone-700 whitespace-pre-wrap">{reply.content}</p>
                      <p className="text-xs text-stone-400 mt-2">
                        {formatDate(reply.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Message Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Message
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={editMessage.subject}
                onChange={(e) => setEditMessage({...editMessage, subject: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={editMessage.content}
                onChange={(e) => setEditMessage({...editMessage, content: e.target.value})}
                rows={5}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateMessage} className="bg-primary hover:bg-primary-800">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Message</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this message? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="bg-red-50 rounded-xl p-4 my-4">
              <p className="font-medium">To: {selectedMessage.recipient_name}</p>
              <p className="text-sm text-stone-600">{selectedMessage.subject}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteMessage}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
